/**
 * Homework overlay: button + full-screen overlay, slide animations, Lenis/parallax suspended while open.
 */

import { gsap } from 'gsap'
import { getAudioManager } from '../../../features/audio'
import { assetUrl, getSceneImagePath, applyResponsiveImage } from '../../../utils/pathResolver'
import track, { OPEN_QUEST, CLOSE_QUEST } from '../../../features/analytics'

function applyNbsp(text: string): string {
  return text.replace(/\b([AaIiKkOoSsUuVvZz]) /g, '$1\u00A0')
}

// --- Types ---

export interface HomeworkContent {
  description: string
  sections?: Array<[string, ...string[]]>
  images?: string[]
}

export interface HomeworkOverlayContext {
  container: HTMLDivElement
  sceneId: string
  content: HomeworkContent
}

export interface HomeworkOverlayRefs {
  button: HTMLElement
  overlay: HTMLElement
  escapeHandler: (e: KeyboardEvent) => void
}

export function setupHomeworkOverlay(context: HomeworkOverlayContext): HomeworkOverlayRefs {
  if (!context.content) throw new Error('Homework content is required')
  const app = document.querySelector('#app') as HTMLElement
  if (!app) throw new Error('App element not found')
  
  // Get current background texture from body or parallax-bg
  // This will be called when overlay opens to get the current texture
  const getCurrentBackgroundTexture = (): string => {
    const parallaxBg = document.querySelector('.parallax-bg') as HTMLElement
    if (parallaxBg && parallaxBg.style.backgroundImage && parallaxBg.style.backgroundImage !== 'none') {
      return parallaxBg.style.backgroundImage
    }
    if (document.body.style.backgroundImage && document.body.style.backgroundImage !== 'none') {
      return document.body.style.backgroundImage
    }
    // Fallback to default texture
    return `url(${assetUrl('/assets/images/texture-dark.jpg')})`
  }

  // --- Build DOM ---
  const homeworkButton = document.createElement('button')
  homeworkButton.className = 'homework-button homework-button--open'
  homeworkButton.setAttribute('aria-label', 'Otevřít úkol')
  homeworkButton.setAttribute('type', 'button')
  
  const buttonImg = document.createElement('img')
  buttonImg.src = assetUrl('/assets/images/quest.svg')
  buttonImg.alt = 'Úkol'
  homeworkButton.appendChild(buttonImg)
  
  // Add button to the scene container so it scrolls with the page
  // For homework scenes, the wrapper should exist (created in constructor)
  // Make container position relative so button can be positioned absolutely
  context.container.style.position = 'relative'
  context.container.appendChild(homeworkButton)
  
  // Create homework overlay
  const overlay = document.createElement('div')
  overlay.className = 'homework-overlay'
  overlay.setAttribute('aria-hidden', 'true')
  overlay.dataset.name = context.sceneId
  
  // Background texture will be set when overlay opens
  
  // Create homework wrapper
  const wrapper = document.createElement('div')
  wrapper.className = 'homework-wrapper'
  
  // Create overlay content
  const overlayContent = document.createElement('div')
  overlayContent.className = 'homework-overlay__content'
  
  // Paper container (clickable — plays text.mp3)
  const paper = document.createElement('button')
  paper.type = 'button'
  paper.className = 'homework-overlay__paper'
  paper.setAttribute('aria-label', 'Přehrát text')
  paper.addEventListener('click', () => {
    const am = getAudioManager()
    am.playCustomSound(am.getChapterTitleSound(), context.sceneId, 'speech')
  })
  
  // Title
  const title = document.createElement('p')
  title.className = 'homework-overlay__title'
  title.textContent = 'Úkol'
  paper.appendChild(title)
  
  const containsHtml = (str: string): boolean => /<[a-z][^>]*>/i.test(str)
  
  const renderContent = (content: string, className: string, parent: HTMLElement): void => {
    if (containsHtml(content)) {
      const container = document.createElement('div')
      container.className = className
      container.innerHTML = applyNbsp(content)
      parent.appendChild(container)
    } else {
      const element = document.createElement('p')
      element.className = className
      element.textContent = applyNbsp(content)
      parent.appendChild(element)
    }
  }
  
  // Description
  if (context.content.description) {
    renderContent(context.content.description, 'homework-overlay__description', paper)
  }
  
  // Sections
  if (context.content.sections && context.content.sections.length > 0) {
    context.content.sections.forEach((section) => {
      const [subtitle, ...contentItems] = section
      
      // Subtitle
      const subtitleEl = document.createElement('p')
      subtitleEl.className = 'homework-overlay__subtitle'
      subtitleEl.textContent = `${applyNbsp(subtitle)}:`
      paper.appendChild(subtitleEl)
      
      for (const content of contentItems) {
        if (content) renderContent(content, 'homework-overlay__description', paper)
      }
    })
  }
  
  overlayContent.appendChild(paper)
  
  // Close button (left side, flipped)
  const closeButton = document.createElement('button')
  closeButton.className = 'homework-button homework-button--close'
  closeButton.setAttribute('aria-label', 'Zavřít úkol')
  closeButton.setAttribute('type', 'button')
  
  const closeButtonImg = document.createElement('img')
  closeButtonImg.src = assetUrl('/assets/images/quest.svg')
  closeButtonImg.alt = 'Zavřít'
  closeButtonImg.style.transform = 'scaleX(-1)'
  closeButton.appendChild(closeButtonImg)
  
  // Add content and button to wrapper
  wrapper.appendChild(overlayContent)
  wrapper.appendChild(closeButton)
  
  // Add wrapper to overlay
  overlay.appendChild(wrapper)

  if (context.content.images?.length) {
    for (const filename of context.content.images) {
      const img = document.createElement('img')
      img.src = getSceneImagePath(context.sceneId, filename)
      img.alt = ''
      img.className = 'homework-overlay__image'
      applyResponsiveImage(img)
      overlay.appendChild(img)
    }
  }

  app.appendChild(overlay)

  // --- Open/close (animations + Lenis/parallax suspend) ---
  let isOpen = false
  const win = window as Window & {
    __lenis?: { off: (e: string, h: () => void) => void; on: (e: string, h: (e: { scroll: number }) => void) => void; stop: () => void; start: () => void; scroll: number }
    __parallaxScrollHandler?: (e: { scroll: number }) => void
    __parallaxWindowScrollHandler?: () => void
  }

  const suspendLenisForOverlay = () => {
    const lenis = win.__lenis
    const parallaxHandler = win.__parallaxScrollHandler
    if (lenis && parallaxHandler) lenis.off('scroll', parallaxHandler)
    if (win.__parallaxWindowScrollHandler) window.removeEventListener('scroll', win.__parallaxWindowScrollHandler)
    const prevent = (e: Event) => e.stopPropagation()
    overlay.addEventListener('wheel', prevent, { passive: false, capture: true })
    overlay.addEventListener('touchstart', prevent, { passive: false, capture: true })
    overlay.addEventListener('touchmove', prevent, { passive: false, capture: true })
    overlay.addEventListener('touchend', prevent, { passive: false, capture: true })
    ;(overlay as HTMLElement & { __preventLenisHandlers?: (e: Event) => void }).__preventLenisHandlers = prevent
    if (lenis) lenis.stop()
  }

  const restoreLenisAfterOverlay = () => {
    const lenis = win.__lenis
    const parallaxHandler = win.__parallaxScrollHandler
    if (lenis && parallaxHandler) {
      lenis.on('scroll', parallaxHandler)
      parallaxHandler({ scroll: lenis.scroll })
    }
    if (win.__parallaxWindowScrollHandler) {
      window.addEventListener('scroll', win.__parallaxWindowScrollHandler, { passive: true })
      win.__parallaxWindowScrollHandler()
    }
    const prevent = (overlay as HTMLElement & { __preventLenisHandlers?: (e: Event) => void }).__preventLenisHandlers
    if (prevent) {
      overlay.removeEventListener('wheel', prevent, { capture: true })
      overlay.removeEventListener('touchstart', prevent, { capture: true })
      overlay.removeEventListener('touchmove', prevent, { capture: true })
      overlay.removeEventListener('touchend', prevent, { capture: true })
      delete (overlay as HTMLElement & { __preventLenisHandlers?: (e: Event) => void }).__preventLenisHandlers
    }
    if (lenis) lenis.start()
  }

  // --- Focus trap ---
  const getFocusableElements = (): HTMLElement[] =>
    Array.from(overlay.querySelectorAll<HTMLElement>(
      'button:not([disabled]), [href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
    ))

  const handleFocusTrap = (e: KeyboardEvent) => {
    if (e.key !== 'Tab') return
    const focusable = getFocusableElements()
    if (focusable.length === 0) return

    const first = focusable[0]
    const last = focusable[focusable.length - 1]

    if (e.shiftKey) {
      if (document.activeElement === first) {
        e.preventDefault()
        last.focus()
      }
    } else {
      if (document.activeElement === last) {
        e.preventDefault()
        first.focus()
      }
    }
  }

  const openOverlay = () => {
    if (isOpen) return
    isOpen = true
    overlay.setAttribute('aria-hidden', 'false')
    
    const backgroundTexture = getCurrentBackgroundTexture()
    overlay.style.backgroundImage = backgroundTexture
    overlay.style.backgroundRepeat = 'repeat'
    overlay.style.backgroundPosition = 'center top'
    overlay.style.backgroundSize = '100%'
    
    getAudioManager().fadeOutSpeechAndCustom()
    suspendLenisForOverlay()
    document.addEventListener('keydown', handleFocusTrap)
    const gameScreen = document.querySelector('.game-screen') as HTMLElement
    if (gameScreen) gsap.to(gameScreen, { x: '-100%', duration: 0.5, ease: 'power2.inOut' })
    gsap.fromTo(overlay, { x: '100%' }, { x: '0%', duration: 0.5, ease: 'power2.inOut', onComplete: () => {
      document.body.style.overflow = 'hidden'
      overlay.scrollTop = 0
      closeButton.focus({ preventScroll: true })
    } })
    gsap.to(homeworkButton, { opacity: 0, pointerEvents: 'none', duration: 0.3 })
  }

  const closeOverlay = () => {
    if (!isOpen) return
    isOpen = false
    document.body.style.overflow = ''
    getAudioManager().fadeOutSpeechAndCustom()
    restoreLenisAfterOverlay()
    document.removeEventListener('keydown', handleFocusTrap)
    gsap.to(overlay, { x: '100%', duration: 0.5, ease: 'power2.inOut', onComplete: () => overlay.setAttribute('aria-hidden', 'true') })
    const gameScreen = document.querySelector('.game-screen') as HTMLElement
    if (gameScreen) gsap.to(gameScreen, { x: '0%', duration: 0.5, ease: 'power2.inOut', onComplete: () => gsap.set(gameScreen, { clearProps: 'transform' }) })
    gsap.to(homeworkButton, { opacity: 1, pointerEvents: 'auto', duration: 0.3, delay: 0.2 })
    homeworkButton.focus()
  }
  
  homeworkButton.addEventListener('click', () => {
    track(OPEN_QUEST, context.sceneId)
    openOverlay()
  })
  closeButton.addEventListener('click', () => {
    track(CLOSE_QUEST, context.sceneId)
    closeOverlay()
  })
  
  // Close on Escape key
  const handleEscape = (e: KeyboardEvent) => {
    if (e.key === 'Escape' && isOpen) {
      closeOverlay()
    }
  }
  document.addEventListener('keydown', handleEscape)
  
  return {
    button: homeworkButton,
    overlay,
    escapeHandler: handleEscape,
  }
}
